import React, { useState } from 'react';
import { Link } from 'react-router-dom'; 
import {
  Shield, Home,
  AlertTriangle,
  Plus,
  Save,
  Trash2,
  Copy,
  Edit,
  Eye,
  ChevronDown,
  ChevronRight,
  Settings,
  Filter,
  Clock,
  RefreshCw,
  Search
} from 'lucide-react';

const WAFPolicyInterface = () => {
  const [activePolicy, setActivePolicy] = useState('default');
  const [activeRuleCategory, setActiveRuleCategory] = useState(null);
  
  const [formData, setFormData] = useState({
    activePolicy: '',
    activeRuleCategory: '',
    policyMode: '',
    defaultAction: '',
    selectedRules: [], // An array to hold selected rules
    customRulePattern: '',
    responseStatus: '',
    customResponseBody: ''
  });

  const [errorMessages, setErrorMessages] = useState([]);

    // Sample policies
    const policies = [
      { id: 'default', name: 'Default Policy', status: 'active', rules: 45 },
      { id: 'strict', name: 'Strict Security', status: 'active', rules: 72 },
      { id: 'custom', name: 'Custom E-commerce', status: 'disabled', rules: 58 }
    ];
  
    // Sample rule categories
    const initialRuleCategories = [
      {
          name: "SQL Injection Protection",
          enabled: true,
          severity: "high",
          rules: [
              { id: 1, name: "", action: "block", status: "enabled" },
             
          ]
      },
      {
          name: "Cross-Site Scripting (XSS) Protection",
          enabled: true,
          severity: "high",
          rules: [
              { id: 4, name: "", action: "block", status: "enabled" },
             
          ]
      },
      {
          name: "Cross-Site Request Forgery (CSRF) Protection",
          enabled: true,
          severity: "medium",
          rules: [
              { id: 7, name: "", action: "monitor", status: "enabled" },
             
          ]
      },
      {
          name: "Remote Code Execution (RCE) Protection",
          enabled: true,
          severity: "critical",
          rules: [
              { id: 9, name: "", action: "block", status: "enabled" },
             
          ]
      },
      {
        name: 'File Inclusion Protection',
        enabled: true,
        severity: 'high',
        rules: [
            { id: 1, name: '', action: 'block', status: 'enabled' },           
        ]
    },
    {
        name: 'Denial of Service (DoS) Protection',
        enabled: true,
        severity: 'medium',
        rules: [
            { id: 1, name: '', action: 'block', status: 'enabled' },
            { id: 2, name: 'Rate Limiting', action: 'throttle', status: 'enabled' },
            { id: 3, name: 'IP Blacklisting', action: 'block', status: 'enabled' }
        ]
    },
      {
          name: "Directory Traversal Protection",
          enabled: true,
          severity: "high",
          rules: [
            { id: 1, name: '', action: 'block', status: 'enabled' },
          ]
      },
      {
          name: "Brute Force & Rate Limiting",
          enabled: true,
          severity: "critical",
          rules: [
            { id: 1, name: '', action: 'block', status: 'enabled' },
          ]
      }
  ];
  const [ruleCategories, setRuleCategories] = useState(initialRuleCategories);

  const handleSaveChanges = () => {
    const errors = [];

    if (!formData.activeRuleCategory) {
      errors.push('You must select a rule category.');
    }
    if (!formData.policyMode) {
      errors.push('You must select a policy mode.');
    }
    if (!formData.defaultAction) {
      errors.push('You must select a default action.');
    }
    if (!formData.customRulePattern) {
      errors.push('Custom rule pattern is required.');
    }
    if (!formData.responseStatus) {
      errors.push('Response status is required.');
    }
    if (!formData.customResponseBody) {
      errors.push('Custom response body is required.');
    }

    if (errors.length) {
      setErrorMessages(errors);
      setTimeout(() => {
        setErrorMessages([]);
      }, 5000);
    } else {
      setErrorMessages([]);
      console.log('Collected Data for submission:', formData);
  
      fetch('/firewall/save-waf-rules', { // Updated endpoint
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
      })
          .then(response => {
              if (!response.ok) {
                  // Parse the error response from the server
                  return response.json().then(err => {
                      throw err; // Re-throw the error to be caught in the catch block
                  });
              }
                return response.json();
              })
              .then(data => {
                // Handle success
                alert(data.message || 'Rule saved successfully!');   

                // Clear all fields
                setFormData({ // Use setFormData to update the state
                activePolicy: '',
                activeRuleCategory: '',
                policyMode: '',
                defaultAction: '',
                selectedRules: [],
                customRulePattern: '',
                responseStatus: '',
                customResponseBody: ''
                });

                // Reload the page
                window.location.reload();
              })
              .catch(error => {
              // Handle error
              console.error('Error saving rule:', error);
  
              // Display error messages from the server
              if (error.errors) {
                  setErrorMessages(error.errors);  // Assuming server sends an 'errors' array
              } else if (error.message) {
                  setErrorMessages([error.message]); // Or a single 'message' string
              } else {
                  setErrorMessages(['An unexpected error occurred.']);
              }
              setTimeout(() => {
                setErrorMessages([]);
              }, 5000);
          });
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleRuleSelection = (ruleId) => {
    setFormData((prevData) => {
      const selectedRules = [...prevData.selectedRules];
      if (selectedRules.includes(ruleId)) {
        const index = selectedRules.indexOf(ruleId);
        selectedRules.splice(index, 1); // Remove if already selected
      } else {
        selectedRules.push(ruleId); // Add new selection
      }
      return { ...prevData, selectedRules };
    });
  };


   // Handler for rule name changes
   const handleRuleNameChange = (categoryId, ruleId, newName) => {
    setRuleCategories((prevCategories) =>
      prevCategories.map((category) => {
        if (category.name === categoryId) {
          return {
            ...category,
            rules: category.rules.map((rule) => {
              if (rule.id === ruleId) {
                return { ...rule, name: newName };
              }
              return rule;
            }),
          };
        }
        return category;
      })
    );
  };


  /*
  const formData = {
  activeRuleCategory: "SQL Injection Protection", // Selected rule category
  policyMode: "Enforcement",                       // Selected policy mode
  defaultAction: "Block",                          // Selected default action
  customRulePattern: "^SELECT.*FROM",             // User-defined regex pattern
  responseStatus: "403 Forbidden",                 // Selected HTTP response status
  customResponseBody: "Access denied due to suspicious activity." // Custom response body
};

  */

 
 
  const renderErrorMessages = () => (
    errorMessages.length > 0 && (
        <div className="mb-4">
            {errorMessages.map((error, index) => {
                // Check if the error is an object and extract the message,
                // otherwise use the error directly
                const errorMessage = typeof error === 'object' && error !== null && error.message ? error.message : String(error);

                return (
                    <div key={index} className="bg-red-600 text-white p-2 rounded-md">
                        <AlertTriangle size={16} className="inline mr-2" />
                        {errorMessage}
                    </div>
                );
            })}
        </div>
    )
);

  const renderPolicyEditor = () => (
    <div className="flex-1 flex">
      {/* Policy List */}
      <div className="w-64 bg-gray-800 border-r border-gray-700 overflow-y-auto">
        <div className="p-4">
          <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center gap-2">
            <Plus size={20} />
            New Policy
          </button>
        </div>

        <div className="space-y-2 p-4">
          {policies.map(policy => (
            <button
              key={policy.id}
              onClick={() => setActivePolicy(policy.id) || setFormData(prev => ({ ...prev, activePolicy: policy.id }))}
              className={`w-full text-left p-3 rounded-md transition-colors flex items-center justify-between ${
                activePolicy === policy.id ? 'bg-gray-700' : 'hover:bg-gray-700'
              }`}
            >
              <div>
                <div className="font-medium text-gray-100">{policy.name}</div>
                <div className="text-sm text-gray-400">{policy.rules} rules</div>
              </div>
              <div className={`w-2 h-2 rounded-full ${policy.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`} />
            </button>
          ))}
        </div>
      </div>

      {/* Rule Configuration */}
      <div className="flex-1 bg-gray-900 overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-100">Rule Configuration</h2>
              <p className="text-gray-400">Configure security rules and responses</p>
            </div>
            <div className="flex gap-4">
              <button onClick={handleSaveChanges} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2">
                <Save size={20} />
                Save Changes
              </button>
              <button className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md flex items-center gap-2">
                <Copy size={20} />
                Clone Policy
              </button>
            </div>
          </div>

          {renderErrorMessages()}

          {/* Policy Settings */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h3 className="text-xl font-semibold mb-4">Policy Settings</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-400 mb-2">Policy Mode</label>
                <select
                  name="policyMode"
                  onChange={handleChange}
                  className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Mode</option>
                  <option value="Prevention">Prevention (Block)</option>
                  <option value="Detection">Detection (Alert Only)</option>
                  <option value="Learning">Learning Mode</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Default Action</label>
                <select
                  name="defaultAction"
                  onChange={handleChange}
                  className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Action</option>
                  <option value="Block">Block Request</option>
                  <option value="Alert">Alert Only</option>
                  <option value="Challenge">CAPTCHA Challenge</option>
                </select>
              </div>
            </div>
          </div>

          {/* Rule Category Selector */}
          <div className="mb-6">
            <label className="block text-gray-400 mb-2">Select Rule Category:</label>
            <select
              name="activeRuleCategory"
              onChange={(e) => {
                setActiveRuleCategory(e.target.value);
                handleChange(e); // Update form state here as well
              }}
              className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a Category</option>
              {ruleCategories.map(category => (
                <option key={category.name} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Display only the selected Rule Category */}
          <div className="space-y-4">
            {activeRuleCategory && ruleCategories
              .filter(category => category.name === activeRuleCategory)
              .map(category => (
                <div key={category.name} className="bg-gray-800 rounded-lg overflow-hidden">
                  <div className="p-4 flex items-center justify-between border-b border-gray-700">
                    <div className="flex items-center gap-4">
                      <Shield size={20} className={category.enabled ? 'text-green-500' : 'text-gray-500'} />
                      <div>
                        <h4 className="font-medium text-gray-100">{category.name}</h4>
                        <p className="text-sm text-gray-400">{category.rules.length} rules</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        category.severity === 'high' ? 'bg-red-900 text-red-300' : 
                          category.severity === 'medium' ? 'bg-yellow-900 text-yellow-300' : 
                          'bg-blue-900 text-blue-300'
                      }`}>
                        {category.severity}
                      </span>
                      <button className="p-2 hover:bg-gray-700 rounded">
                        <ChevronDown size={20} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-gray-400">
                          <th className="pb-2">Rule Name</th>
                          <th className="pb-2">Action</th>
                          <th className="pb-2">Status</th>
                          <th className="pb-2">Select</th>
                        </tr>
                      </thead>
                      <tbody>
                      {category.rules.map((rule) => (
                        <tr key={rule.id} className="border-t border-gray-700">
                          <td>
                            <input
                              type="text"
                              value={rule.name}
                              onChange={(e) =>
                                handleRuleNameChange(category.name, rule.id, e.target.value)
                              }
                              className="bg-gray-800 text-gray-100 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                            />
                          </td>
                            <td>
                              <select className="bg-gray-700 text-gray-200 px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option>Block</option>
                                <option>Alert</option>
                                <option>Log</option>
                              </select>
                            </td>
                            <td>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                rule.status === 'enabled' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'
                              }`}>
                                {rule.status}
                              </span>
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                value={rule.name}
                                onChange={() => handleRuleSelection(rule.name)}
                                checked={formData.selectedRules.includes(rule.name)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Rule Details Panel */}
      <div className="w-80 bg-gray-800 border-l border-gray-700">
        <div className="p-6">
          <h3 className="text-xl font-semibold mb-4">Rule Details</h3>
          <div className="space-y-6">
            <div>
              <label className="block text-gray-400 mb-2">Custom Rule Pattern</label>
              <textarea 
                name="customRulePattern"
                onChange={handleChange}
                className="w-full h-32 bg-gray-700 text-gray-200 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter regex pattern..."
              />
            </div>
            <div>
              <label className="block text-gray-400 mb-2">Response Status</label>
              <select
                name="responseStatus"
                onChange={handleChange}
                className="w-full bg-gray-700 text-gray-200 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Status</option>
                <option value="403">403 Forbidden</option>
                <option value="401">401 Unauthorized</option>
                <option value="200">200 OK (Silent Block)</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 mb-2">Custom Response Body</label>
              <textarea 
                name="customResponseBody"
                onChange={handleChange}
                className="w-full h-32 bg-gray-700 text-gray-200 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter custom response..."
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-gray-900 text-gray-300 flex flex-col">
      {/* Top Bar */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            
            <h1 className="text-2xl font-bold text-white">WAF Policy Management</h1>
            <span className="px-3 py-1 rounded-full text-xs bg-green-900 text-green-300">
              Active
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/WAF"> 
                <Home size={20} color="white" />
              </Link>
            <button className="p-2 hover:bg-gray-700 rounded-md" onClick={() => window.location.reload()}> 
              <RefreshCw size={20} />
            </button>
            <button className="p-2 hover:bg-gray-700 rounded-md">
              <Settings size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      {renderPolicyEditor()}
    </div>
  );
};

export default WAFPolicyInterface;


/*


The `formData` object you provided is a JavaScript object that represents a set of data or configuration settings that a user might submit through a form. Each key in this object corresponds to a specific attribute that the form is collecting data for, and each value is the input or selection made by the user. Let’s break down what each property means, using the example values provided:

### Breakdown of Example `formData` Object

```javascript
const formData = {
  activeRuleCategory: "SQL Injection Protection", // Selected rule category
  policyMode: "Enforcement",                       // Selected policy mode
  defaultAction: "Block",                          // Selected default action
  customRulePattern: "^SELECT.*FROM",             // User-defined regex pattern
  responseStatus: "403 Forbidden",                 // Selected HTTP response status
  customResponseBody: "Access denied due to suspicious activity." // Custom response body
};
```

1. **activeRuleCategory**: 
   - **Value**: `"SQL Injection Protection"`
   - **Meaning**: This indicates that the user has selected a specific category of rules to apply for their data protection or filtering. In this case, it is focused on protecting against SQL injection attacks.

2. **policyMode**: 
   - **Value**: `"Enforcement"`
   - **Meaning**: This defines the mode in which the policy will operate. "Enforcement" suggests that the rules will actively block or restrict actions that violate the conditions set by the rule. The alternative might be "Monitoring," where actions are logged but not blocked.

3. **defaultAction**: 
   - **Value**: `"Block"`
   - **Meaning**: This specifies what action should be taken by default when a rule is triggered. In this case, if an incoming request matches any defined rules (like potential SQL injections), the action will be to block the request.

4. **customRulePattern**: 
   - **Value**: `"^SELECT.*FROM"`
   - **Meaning**: This represents a user-defined custom regex (regular expression) pattern that specifies what constitutes suspicious behavior. This particular pattern checks for SQL statements that start with "SELECT" and include "FROM", a common structure in SQL queries. Essentially, it helps to identify potentially malicious SQL queries.

5. **responseStatus**: 
   - **Value**: `"403 Forbidden"`
   - **Meaning**: This indicates the HTTP status code that should be returned when an action is blocked. A status of "403 Forbidden" tells the client (e.g., browser or API consumer) that their request was understood but is being refused due to security rules.

6. **customResponseBody**: 
   - **Value**: `"Access denied due to suspicious activity."`
   - **Meaning**: This is the message that will be sent back to a user or client when their request is blocked. In this case, it provides a clear reason as to why their access was denied.

### Overall Context

This `formData` object encapsulates the settings and rules a user has configured for a security feature, possibly in a web application that handles data securely. The user is essentially defining how the application should behave when malicious requests are detected, with specific actions to take, patterns to evaluate, and responses to send back.

When the `formData` is submitted (after validation), the application would use these settings to enforce security measures, allowing it to effectively prevent SQL injection attacks while providing feedback to the user/end client regarding the status of their request or actions taken.

*/